import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import {
  ChevronLeft, ChevronRight, ZoomIn, ZoomOut, X, Loader2,
  Bookmark, BookmarkCheck, ChevronsLeft, ChevronsRight,
  Maximize, Minimize, List, RotateCw, Images, Maximize2
} from 'lucide-react';
import { Button } from './ui/button';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const BACKEND_URL = window.location.origin;

// Full static class strings so Tailwind JIT keeps them
const TONES = {
  pink: 'bg-gradient-to-br from-pink-500 to-rose-500 shadow-pink-500/40',
  purple: 'bg-gradient-to-br from-purple-500 to-fuchsia-500 shadow-purple-500/40',
  cyan: 'bg-gradient-to-br from-cyan-500 to-blue-500 shadow-cyan-500/40',
  amber: 'bg-gradient-to-br from-amber-400 to-orange-500 shadow-amber-500/40',
  emerald: 'bg-gradient-to-br from-emerald-500 to-teal-500 shadow-emerald-500/40',
  indigo: 'bg-gradient-to-br from-indigo-500 to-violet-500 shadow-indigo-500/40',
  red: 'bg-gradient-to-br from-red-500 to-rose-600 shadow-red-500/40',
  slate: 'bg-gradient-to-br from-slate-600 to-slate-700 shadow-slate-900/40',
};

const ToolBtn = ({ icon: Icon, onClick, title, color = 'pink', active = false, disabled = false, testid, big = false }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    title={title}
    data-testid={testid}
    className={`group relative ${big ? 'h-12 w-12' : 'h-11 w-11'} rounded-2xl flex items-center justify-center text-white shadow-lg
      transition-all duration-200 ease-out hover:scale-110 hover:brightness-110 active:scale-95
      disabled:opacity-25 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:brightness-100
      ${active ? 'ring-2 ring-white/70 scale-105' : ''} ${TONES[color]}`}
  >
    <Icon className={`${big ? 'w-6 h-6' : 'w-5 h-5'} drop-shadow`} />
    <span className="pointer-events-none absolute -bottom-9 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-black/90 px-2 py-1 text-[11px] text-white opacity-0 translate-y-1 transition-all duration-200 group-hover:opacity-100 group-hover:translate-y-0 z-50">
      {title}
    </span>
  </button>
);

const SecurePDFViewer = ({ bookId, bookTitle, onClose }) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [rotation, setRotation] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [bookmarks, setBookmarks] = useState([]);
  const [showBookmarks, setShowBookmarks] = useState(false);
  const [showThumbs, setShowThumbs] = useState(false);
  const [pageInput, setPageInput] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const controlsTimer = useRef(null);
  const touchStart = useRef(null);
  const saveTimer = useRef(null);

  // Fetch PDF
  useEffect(() => {
    const fetchPDF = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch(`${BACKEND_URL}/api/books/${bookId}/secure-read`, {
          credentials: 'include'
        });
        if (!response.ok) {
          let errMsg = 'Failed to load book';
          try { const d = JSON.parse(await response.text()); errMsg = d.detail || errMsg; } catch (e) {}
          throw new Error(errMsg);
        }
        const blob = await response.blob();
        setPdfUrl(URL.createObjectURL(blob));
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };
    fetchPDF();
    return () => { if (pdfUrl) URL.revokeObjectURL(pdfUrl); };
  }, [bookId]);

  // Load saved progress
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/books/${bookId}/reading-progress`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        if (d.current_page > 1) setPageNumber(d.current_page);
        if (d.bookmarks?.length) setBookmarks(d.bookmarks);
      })
      .catch(() => {});
  }, [bookId]);

  // Auto-save progress (debounced)
  const saveProgress = useCallback(() => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      fetch(`${BACKEND_URL}/api/books/${bookId}/reading-progress`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ current_page: pageNumber, bookmarks })
      }).catch(() => {});
    }, 1000);
  }, [bookId, pageNumber, bookmarks]);

  useEffect(() => { saveProgress(); }, [pageNumber, bookmarks, saveProgress]);

  const goToPrevPage = () => setPageNumber(p => Math.max(p - 1, 1));
  const goToNextPage = () => setPageNumber(p => Math.min(p + 1, numPages || 1));
  const zoomIn = () => setScale(p => Math.min(p + 0.25, 3));
  const zoomOut = () => setScale(p => Math.max(p - 0.25, 0.5));
  const resetZoom = () => setScale(1.0);
  const rotate = () => setRotation(r => (r + 90) % 360);

  // Keyboard & security
  useEffect(() => {
    const handleKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && ['s', 'p', 'S', 'P'].includes(e.key)) e.preventDefault();
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); goToNextPage(); }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); goToPrevPage(); }
      if (e.key === 'Home') { e.preventDefault(); setPageNumber(1); }
      if (e.key === 'End' && numPages) { e.preventDefault(); setPageNumber(numPages); }
      if (e.key === 'Escape') onClose();
      if (e.key === '+' || e.key === '=') { e.preventDefault(); zoomIn(); }
      if (e.key === '-') { e.preventDefault(); zoomOut(); }
      if (e.key === 'r' || e.key === 'R') { e.preventDefault(); rotate(); }
    };
    const preventCtx = (e) => e.preventDefault();
    document.addEventListener('keydown', handleKey);
    document.addEventListener('contextmenu', preventCtx);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('contextmenu', preventCtx);
    };
  }, [numPages, pageNumber]);

  // Touch swipe
  const handleTouchStart = (e) => { touchStart.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    if (!touchStart.current) return;
    const diff = touchStart.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 60) { diff > 0 ? goToNextPage() : goToPrevPage(); }
    touchStart.current = null;
  };

  // Auto-hide controls
  const resetControlsTimer = () => {
    setShowControls(true);
    if (controlsTimer.current) clearTimeout(controlsTimer.current);
    controlsTimer.current = setTimeout(() => setShowControls(false), 5000);
  };

  const onDocumentLoadSuccess = useCallback(({ numPages: n }) => setNumPages(n), []);
  const onDocumentLoadError = useCallback((err) => {
    if (pdfjs.GlobalWorkerOptions.workerSrc.includes('unpkg')) {
      pdfjs.GlobalWorkerOptions.workerSrc = `/pdf.worker.min.js`;
      setLoading(true); setError(null);
      fetch(`${BACKEND_URL}/api/books/${bookId}/secure-read`, { credentials: 'include' })
        .then(r => r.blob())
        .then(blob => { setPdfUrl(URL.createObjectURL(blob)); setLoading(false); })
        .catch(() => setError('Failed to load book.'));
    } else { setError('Failed to render PDF.'); }
  }, [bookId]);

  const toggleBookmark = () => {
    setBookmarks(prev =>
      prev.includes(pageNumber)
        ? prev.filter(p => p !== pageNumber)
        : [...prev, pageNumber].sort((a, b) => a - b)
    );
  };

  const goToPage = (p) => {
    const num = parseInt(p);
    if (num >= 1 && num <= (numPages || 1)) setPageNumber(num);
    setPageInput('');
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  };

  const progress = numPages ? Math.round((pageNumber / numPages) * 100) : 0;
  const isBookmarked = bookmarks.includes(pageNumber);
  const sidePanelWidth = (showThumbs ? 150 : 0) + (showBookmarks ? 224 : 0);
  const pageWidth = Math.min(window.innerWidth - sidePanelWidth - 48, 1000);
  const thumbPages = numPages ? Array.from({ length: numPages }, (_, i) => i + 1) : [];

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 bg-gradient-to-br from-[#160d28] via-[#1a1a2e] to-[#0d1424] flex items-center justify-center">
        <div className="text-center">
          <div className="relative w-20 h-20 mx-auto mb-6">
            <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-pink-500 to-purple-500 blur-xl opacity-60 animate-pulse" />
            <Loader2 className="relative w-20 h-20 text-pink-400 animate-spin" />
          </div>
          <p className="text-white text-lg font-medium" dir="rtl">جاري تحميل الكتاب...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 z-50 bg-gradient-to-br from-[#160d28] to-[#0d1424] flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
            <X className="w-9 h-9 text-red-400" />
          </div>
          <p className="text-white text-lg mb-2" dir="rtl">خطأ في تحميل الكتاب</p>
          <p className="text-gray-400 mb-6">{error}</p>
          <div className="flex gap-3 justify-center">
            <Button onClick={() => { setError(null); setLoading(true); setPdfUrl(null);
              fetch(`${BACKEND_URL}/api/books/${bookId}/secure-read`, { credentials: 'include' })
                .then(r => r.blob()).then(b => { setPdfUrl(URL.createObjectURL(b)); setLoading(false); })
                .catch(() => { setError('Failed to load.'); setLoading(false); });
            }} className="bg-gray-700 hover:bg-gray-600">إعادة المحاولة</Button>
            <Button onClick={onClose} className="bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700">إغلاق</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 z-50 bg-gradient-to-br from-[#160d28] via-[#15152b] to-[#0d1424] flex flex-col"
      style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
      onMouseMove={resetControlsTimer}
    >
      {/* Top Bar */}
      <div className={`relative bg-[#0f0a1a]/90 backdrop-blur-xl border-b border-white/10 px-4 py-3 flex flex-wrap items-center justify-between gap-3 transition-all duration-300 ${showControls ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2 pointer-events-none'}`}>
        {/* Title + close */}
        <div className="flex items-center gap-3 min-w-0">
          <ToolBtn icon={X} onClick={onClose} title="إغلاق" color="red" testid="pdf-close-btn" big />
          <div className="min-w-0">
            <h2 className="text-white font-bold truncate text-base max-w-[40vw]" dir="rtl">{bookTitle}</h2>
            <p className="text-pink-300/70 text-xs font-mono">{progress}% • صفحة {pageNumber} / {numPages || '...'}</p>
          </div>
        </div>

        {/* Center: navigation */}
        <div className="flex items-center gap-2">
          <ToolBtn icon={ChevronsLeft} onClick={() => setPageNumber(1)} disabled={pageNumber <= 1} title="الأولى" color="purple" />
          <ToolBtn icon={ChevronLeft} onClick={goToPrevPage} disabled={pageNumber <= 1} title="السابق" color="pink" testid="pdf-prev-page" />
          <div className="flex items-center gap-1.5 px-2">
            <input
              type="text"
              value={pageInput || pageNumber}
              onChange={(e) => setPageInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') goToPage(pageInput); }}
              onBlur={() => { if (pageInput) goToPage(pageInput); }}
              onFocus={() => setPageInput(String(pageNumber))}
              className="w-14 h-11 bg-white/10 border-2 border-pink-500/40 rounded-xl text-center text-white text-base font-bold font-mono focus:border-pink-500 focus:outline-none focus:ring-2 focus:ring-pink-500/30 transition-all"
              data-testid="pdf-page-input"
            />
            <span className="text-gray-400 text-sm font-mono">/ {numPages || '...'}</span>
          </div>
          <ToolBtn icon={ChevronRight} onClick={goToNextPage} disabled={pageNumber >= (numPages || 1)} title="التالي" color="pink" testid="pdf-next-page" />
          <ToolBtn icon={ChevronsRight} onClick={() => numPages && setPageNumber(numPages)} disabled={pageNumber >= (numPages || 1)} title="الأخيرة" color="purple" />
        </div>

        {/* Right: tools */}
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <ToolBtn icon={Images} onClick={() => { setShowThumbs(!showThumbs); setShowBookmarks(false); }} active={showThumbs} title="المصغرات" color="indigo" testid="pdf-thumbnails-btn" />
          <div className="flex items-center gap-2 px-2 rounded-2xl bg-white/5">
            <ToolBtn icon={ZoomOut} onClick={zoomOut} title="تصغير" color="cyan" testid="pdf-zoom-out" />
            <button onClick={resetZoom} title="إعادة الضبط" className="text-white text-sm font-bold font-mono min-w-[48px] text-center hover:text-cyan-300 transition-colors" data-testid="pdf-zoom-reset">
              {Math.round(scale * 100)}%
            </button>
            <ToolBtn icon={ZoomIn} onClick={zoomIn} title="تكبير" color="cyan" testid="pdf-zoom-in" />
          </div>
          <ToolBtn icon={RotateCw} onClick={rotate} title="تدوير" color="emerald" testid="pdf-rotate-btn" />
          <ToolBtn icon={isBookmarked ? BookmarkCheck : Bookmark} onClick={toggleBookmark} active={isBookmarked} title="إشارة مرجعية" color="amber" testid="pdf-bookmark-btn" />
          <ToolBtn icon={List} onClick={() => { setShowBookmarks(!showBookmarks); setShowThumbs(false); }} active={showBookmarks} title="الإشارات المرجعية" color="amber" testid="pdf-bookmarks-list-btn" />
          <ToolBtn icon={isFullscreen ? Minimize : Maximize} onClick={toggleFullscreen} title="ملء الشاشة" color="indigo" testid="pdf-fullscreen" />
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1.5 bg-white/5 relative overflow-hidden">
        <div className="h-full bg-gradient-to-r from-pink-500 via-fuchsia-500 to-purple-500 transition-all duration-500 shadow-[0_0_12px_rgba(236,72,153,0.7)]" style={{ width: `${progress}%` }} />
      </div>

      {/* Main content area */}
      <div className="flex flex-1 overflow-hidden relative">
        {/* Thumbnails sidebar */}
        {showThumbs && pdfUrl && (
          <div className="w-[150px] bg-[#0f0a1a]/95 backdrop-blur-md border-r border-white/10 overflow-y-auto shrink-0 p-2" data-testid="pdf-thumbnails-panel">
            <Document file={pdfUrl} loading={<div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 text-pink-500 animate-spin" /></div>}>
              {thumbPages.map((p) => (
                <button
                  key={p}
                  onClick={() => setPageNumber(p)}
                  className={`block w-full mb-2 rounded-lg overflow-hidden transition-all duration-200 hover:scale-[1.03] ${p === pageNumber ? 'ring-2 ring-pink-500 shadow-lg shadow-pink-500/30' : 'ring-1 ring-white/10 hover:ring-pink-400/50'}`}
                  data-testid={`pdf-thumb-${p}`}
                >
                  <Page pageNumber={p} width={130} renderTextLayer={false} renderAnnotationLayer={false}
                    loading={<div className="h-40 bg-white/5 flex items-center justify-center"><span className="text-gray-600 text-xs">{p}</span></div>} />
                  <span className="block text-center text-[11px] text-gray-400 py-0.5 bg-black/30 font-mono">{p}</span>
                </button>
              ))}
            </Document>
          </div>
        )}

        {/* Bookmarks sidebar */}
        {showBookmarks && (
          <div className="w-56 bg-[#0f0a1a]/95 backdrop-blur-md border-r border-white/10 overflow-y-auto shrink-0">
            <div className="p-4">
              <h3 className="text-white font-bold text-sm mb-3 flex items-center gap-2">
                <BookmarkCheck className="w-5 h-5 text-amber-400" />
                الإشارات ({bookmarks.length})
              </h3>
              {bookmarks.length === 0 ? (
                <p className="text-gray-500 text-xs leading-relaxed">لا توجد إشارات مرجعية. اضغط على أيقونة الإشارة لإضافة واحدة.</p>
              ) : (
                <div className="space-y-1.5">
                  {bookmarks.map((p) => (
                    <button
                      key={p}
                      onClick={() => { setPageNumber(p); }}
                      className={`w-full text-left px-3 py-2.5 rounded-xl text-sm transition-all duration-200 hover:scale-[1.02] ${
                        p === pageNumber
                          ? 'bg-gradient-to-r from-amber-500/30 to-orange-500/20 text-amber-200 border border-amber-500/40'
                          : 'text-gray-300 hover:bg-white/5 border border-transparent'
                      }`}
                    >
                      <span className="font-mono flex items-center gap-2"><Bookmark className="w-3.5 h-3.5" /> صفحة {p}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* PDF area */}
        <div
          ref={contentRef}
          className="flex-1 overflow-auto flex justify-center py-8"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onCopy={(e) => e.preventDefault()}
          onCut={(e) => e.preventDefault()}
        >
          {pdfUrl && (
            <Document
              file={pdfUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              loading={
                <div className="flex items-center justify-center h-96">
                  <Loader2 className="w-8 h-8 text-pink-500 animate-spin" />
                </div>
              }
            >
              <Page
                pageNumber={pageNumber}
                scale={scale}
                rotate={rotation}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                className="shadow-[0_20px_60px_rgba(0,0,0,0.6)] rounded-lg overflow-hidden"
                width={pageWidth}
                loading={
                  <div className="flex items-center justify-center h-96 w-[600px]">
                    <Loader2 className="w-6 h-6 text-pink-500 animate-spin" />
                  </div>
                }
              />
            </Document>
          )}
        </div>

        {/* Side navigation arrows (desktop) */}
        <button
          onClick={goToPrevPage}
          disabled={pageNumber <= 1}
          className={`absolute left-4 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 shadow-xl shadow-pink-500/40 flex items-center justify-center transition-all duration-200 hover:scale-110 active:scale-95 disabled:opacity-0 ${showControls ? 'opacity-80' : 'opacity-0'} hidden md:flex`}
          data-testid="pdf-arrow-prev"
        >
          <ChevronLeft className="w-7 h-7 text-white" />
        </button>
        <button
          onClick={goToNextPage}
          disabled={pageNumber >= (numPages || 1)}
          className={`absolute right-4 top-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 shadow-xl shadow-pink-500/40 flex items-center justify-center transition-all duration-200 hover:scale-110 active:scale-95 disabled:opacity-0 ${showControls ? 'opacity-80' : 'opacity-0'} hidden md:flex`}
          data-testid="pdf-arrow-next"
        >
          <ChevronRight className="w-7 h-7 text-white" />
        </button>
      </div>

      {/* Bottom bar */}
      <div className={`bg-[#0f0a1a]/90 backdrop-blur-xl border-t border-white/10 px-5 py-2.5 flex items-center justify-between transition-all duration-300 ${showControls ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2 pointer-events-none'}`}>
        <p className="text-gray-500 text-xs" dir="rtl">
          © جميع الحقوق محفوظة - د. نواف الشبلي
        </p>
        <div className="flex items-center gap-4">
          <span className="text-pink-300 text-xs font-semibold font-mono">{progress}% مكتمل</span>
          {isBookmarked && <span className="text-amber-400 text-xs flex items-center gap-1"><BookmarkCheck className="w-3.5 h-3.5" /> محفوظة</span>}
        </div>
      </div>

      <style>{`@media print { body * { display: none !important; } }`}</style>
    </div>
  );
};

export default SecurePDFViewer;
